
import React, { useMemo, useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { MathUtils } from 'three';
import { Grid, BuildingType, ZoneType } from '../../types';
import { BUILDINGS } from '../../constants';
import { gridToWorld, getRandomRange } from '../../utils/mapUtils';

const packetGeo = new THREE.BoxGeometry(1, 1, 1); // Base 1, scaled down in instance
const boxGeo = new THREE.BoxGeometry(1, 1, 1);
const carGeo = new THREE.BoxGeometry(0.4, 0.2, 0.6);
const hatGeo = new THREE.ConeGeometry(0.12, 0.15, 8); // Hat geometry

export const PacketSystem = ({ grid, paused = false }: { grid: Grid; paused?: boolean }) => {
  const pipeTiles = useMemo(() => {
    const tiles: {x: number, y: number}[] = [];
    grid.forEach(row => row.forEach(tile => {
      const validTypes = [
          BuildingType.Pipeline, 
          BuildingType.WAF, 
          BuildingType.Vault,
          BuildingType.OneWayDiode,
      ];
      if (validTypes.includes(tile.buildingType) || tile.buildingType === BuildingType.Core) {
          tiles.push({x: tile.x, y: tile.y});
      }
    }));
    return tiles;
  }, [grid]);

  const validCoords = useMemo(() => {
    const set = new Set<string>();
    pipeTiles.forEach(t => set.add(`${t.x},${t.y}`));
    return set;
  }, [pipeTiles]);

  const packetCount = Math.min(pipeTiles.length * 2, 80);
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const packetState = useRef<Float32Array>(new Float32Array(0)); 
  const dummy = useMemo(() => new THREE.Object3D(), []);

  useEffect(() => {
    if (pipeTiles.length < 2) return;
    // x, y, tx, ty, progress, speed, scaleVariant
    packetState.current = new Float32Array(packetCount * 7);
    for (let i = 0; i < packetCount; i++) {
      const startNode = pipeTiles[Math.floor(Math.random() * pipeTiles.length)];
      packetState.current[i*7 + 0] = startNode.x;
      packetState.current[i*7 + 1] = startNode.y;
      packetState.current[i*7 + 2] = startNode.x;
      packetState.current[i*7 + 3] = startNode.y;
      packetState.current[i*7 + 4] = 1.0; 
      packetState.current[i*7 + 5] = getRandomRange(0.02, 0.05); 
      // Varied small sizes: 0.08 to 0.15
      packetState.current[i*7 + 6] = getRandomRange(0.08, 0.15);
    }
  }, [pipeTiles, packetCount]);

  useFrame(() => {
    if (!meshRef.current || pipeTiles.length < 2 || packetState.current.length === 0 || paused) return;

    for (let i = 0; i < packetCount; i++) {
      const idx = i * 7;
      let curX = packetState.current[idx];
      let curY = packetState.current[idx+1];
      let tarX = packetState.current[idx+2];
      let tarY = packetState.current[idx+3];
      let progress = packetState.current[idx+4];
      const speed = packetState.current[idx+5];
      const scale = packetState.current[idx+6];

      progress += speed;

      if (progress >= 1) {
        curX = tarX;
        curY = tarY;
        progress = 0;
        
        const candidates = [
            {x: curX, y: curY - 1}, 
            {x: curX, y: curY + 1}, 
            {x: curX - 1, y: curY}, 
            {x: curX + 1, y: curY}  
        ].filter(c => validCoords.has(`${c.x},${c.y}`));

        if (candidates.length > 0) {
            const next = candidates[Math.floor(Math.random() * candidates.length)];
            tarX = next.x;
            tarY = next.y;
        } else {
            const rnd = pipeTiles[Math.floor(Math.random() * pipeTiles.length)];
            curX = rnd.x; curY = rnd.y; 
            tarX = rnd.x; tarY = rnd.y;
            progress = 1; 
        }
      }

      packetState.current[idx] = curX;
      packetState.current[idx+1] = curY;
      packetState.current[idx+2] = tarX;
      packetState.current[idx+3] = tarY;
      packetState.current[idx+4] = progress;

      const gx = MathUtils.lerp(curX, tarX, progress);
      const gy = MathUtils.lerp(curY, tarY, progress);
      const [wx, _, wz] = gridToWorld(gx, gy);

      dummy.position.set(wx, 0.35, wz);
      // Use the pre-calculated random small size
      dummy.scale.set(scale, scale, scale);
      dummy.rotation.x += 0.1;
      dummy.rotation.y += 0.1;
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return <instancedMesh ref={meshRef} args={[packetGeo, undefined, packetCount]}><meshBasicMaterial color="#60a5fa" /></instancedMesh>;
};

export const AgentSystem = ({ grid, color, zoneTypes, specificType, paused = false }: { grid: Grid, color: string, zoneTypes?: ZoneType[], specificType?: BuildingType, paused?: boolean }) => {
    const tiles = useMemo(() => {
        const t: {x: number, y: number}[] = [];
        grid.forEach(row => row.forEach(tile => {
            if (specificType) {
                if (tile.buildingType === specificType && tile.isConnected) {
                    t.push({x: tile.x, y: tile.y});
                }
            } else if (zoneTypes) {
                const conf = BUILDINGS[tile.buildingType];
                if (conf && zoneTypes.includes(conf.zone) && tile.isConnected) {
                    t.push({x: tile.x, y: tile.y});
                }
            }
        }));
        return t;
    }, [grid, zoneTypes, specificType]);

    const count = Math.min(tiles.length * (specificType ? 5 : 3), 60);
    const bodyMeshRef = useRef<THREE.InstancedMesh>(null);
    const headMeshRef = useRef<THREE.InstancedMesh>(null);
    const hatMeshRef = useRef<THREE.InstancedMesh>(null); 
    
    const dummy = useMemo(() => new THREE.Object3D(), []);
    const state = useRef<Float32Array>(new Float32Array(0));
    
    // Check if these agents are "Important" (Security or Admin types get hats)
    const hasHat = specificType === BuildingType.JumpGate || (zoneTypes && (zoneTypes.includes(ZoneType.Security) || zoneTypes.includes(ZoneType.Core)));

    useEffect(() => {
        if(tiles.length === 0) return;
        state.current = new Float32Array(count * 7); // x, y, tx, ty, progress, speed, randomOffset
        
        // Initialize Colors
        const baseColor = new THREE.Color(color);
        for(let i=0; i<count; i++) {
            const n = tiles[Math.floor(Math.random() * tiles.length)];
            state.current[i*7] = n.x; state.current[i*7+1] = n.y; state.current[i*7+2] = n.x; state.current[i*7+3] = n.y;
            state.current[i*7+5] = getRandomRange(0.01, 0.03);
            state.current[i*7+6] = Math.random() * 100; // Random phase for animation

            // Color variation
            if (bodyMeshRef.current) {
                const variant = baseColor.clone().offsetHSL(0, 0, (Math.random() - 0.5) * 0.2);
                bodyMeshRef.current.setColorAt(i, variant);
            }
            if (headMeshRef.current) {
                // Heads are usually skin tone or robot color, lets vary slightly
                const headCol = new THREE.Color("#fcd34d").offsetHSL(0, 0, (Math.random() - 0.5) * 0.1);
                headMeshRef.current.setColorAt(i, headCol);
            }
            if (hatMeshRef.current && hasHat) {
                hatMeshRef.current.setColorAt(i, new THREE.Color("#1e293b")); // Dark hat
            }
        }
        if(bodyMeshRef.current) bodyMeshRef.current.instanceColor!.needsUpdate = true;
        if(headMeshRef.current) headMeshRef.current.instanceColor!.needsUpdate = true;
        if(hatMeshRef.current) hatMeshRef.current.instanceColor!.needsUpdate = true;

    }, [tiles, count, color, hasHat]);

    useFrame((rState) => {
        if(!bodyMeshRef.current || tiles.length === 0 || paused) return;
        const time = rState.clock.elapsedTime;

        for(let i=0; i<count; i++) {
            const idx = i*7;
            let p = state.current[idx+4] + state.current[idx+5];
            const phase = state.current[idx+6];
            
            // Bobbing animation logic
            const bob = Math.sin(time * 15 + phase) * 0.03;

            if(p >= 1) {
                p=0;
                state.current[idx] = state.current[idx+2];
                state.current[idx+1] = state.current[idx+3];
                const next = tiles[Math.floor(Math.random() * tiles.length)];
                state.current[idx+2] = next.x; state.current[idx+3] = next.y;
            }
            state.current[idx+4] = p;
            
            const [wx, _, wz] = gridToWorld(MathUtils.lerp(state.current[idx], state.current[idx+2], p), MathUtils.lerp(state.current[idx+1], state.current[idx+3], p));
            
            // Look direction
            const lookAngle = Math.atan2(state.current[idx+2] - state.current[idx], state.current[idx+3] - state.current[idx+1]);

            // Body
            dummy.scale.set(0.15, 0.3, 0.15);
            dummy.position.set(wx, 0.35 + bob, wz);
            dummy.rotation.set(0, lookAngle, 0);
            dummy.updateMatrix();
            bodyMeshRef.current.setMatrixAt(i, dummy.matrix);

            // Head (moves with bob)
            dummy.scale.set(0.12, 0.12, 0.12);
            dummy.position.set(wx, 0.55 + bob, wz); // Above body
            dummy.rotation.set(0, lookAngle, 0);
            dummy.updateMatrix();
            headMeshRef.current!.setMatrixAt(i, dummy.matrix);

            // Hat (if important)
            if (hasHat && hatMeshRef.current) {
                dummy.scale.set(1, 1, 1);
                dummy.position.set(wx, 0.65 + bob, wz); // Above head
                dummy.rotation.set(0, lookAngle, 0);
                dummy.updateMatrix();
                hatMeshRef.current.setMatrixAt(i, dummy.matrix);
            }
        }
        bodyMeshRef.current.instanceMatrix.needsUpdate = true;
        headMeshRef.current!.instanceMatrix.needsUpdate = true;
        if(hatMeshRef.current) hatMeshRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <group>
            <instancedMesh ref={bodyMeshRef} args={[boxGeo, undefined, count]}>
                <meshStandardMaterial />
            </instancedMesh>
            <instancedMesh ref={headMeshRef} args={[boxGeo, undefined, count]}>
                <meshStandardMaterial />
            </instancedMesh>
            {hasHat && (
                <instancedMesh ref={hatMeshRef} args={[hatGeo, undefined, count]}>
                    <meshStandardMaterial color="#1e293b" />
                </instancedMesh>
            )}
        </group>
    );
};

export const CarSystem = ({ grid, paused = false }: { grid: Grid; paused?: boolean }) => {
    const roadTiles = useMemo(() => {
        const tiles: {x: number, y: number}[] = [];
        grid.forEach(row => row.forEach(tile => {
            if (tile.buildingType === BuildingType.Road) {
                tiles.push({x: tile.x, y: tile.y});
            }
        }));
        return tiles;
    }, [grid]);

    const validCoords = useMemo(() => {
        const set = new Set<string>();
        roadTiles.forEach(t => set.add(`${t.x},${t.y}`));
        return set;
    }, [roadTiles]);

    const carCount = Math.min(roadTiles.length * 1, 30);
    const meshRef = useRef<THREE.InstancedMesh>(null);
    const carState = useRef<Float32Array>(new Float32Array(0));
    const dummy = useMemo(() => new THREE.Object3D(), []);

    useEffect(() => {
        if (roadTiles.length < 2) return;
        carState.current = new Float32Array(carCount * 6);
        for(let i=0; i<carCount; i++) {
            const start = roadTiles[Math.floor(Math.random() * roadTiles.length)];
            carState.current[i*6] = start.x; carState.current[i*6+1] = start.y;
            carState.current[i*6+2] = start.x; carState.current[i*6+3] = start.y;
            carState.current[i*6+4] = 1.0;
            carState.current[i*6+5] = getRandomRange(0.02, 0.04);
        }
    }, [roadTiles, carCount]);

    useFrame(() => {
        if(!meshRef.current || roadTiles.length < 2 || carState.current.length === 0 || paused) return;

        for(let i=0; i<carCount; i++) {
            const idx = i*6;
            let curX = carState.current[idx];
            let curY = carState.current[idx+1];
            let tarX = carState.current[idx+2];
            let tarY = carState.current[idx+3];
            let progress = carState.current[idx+4];
            const speed = carState.current[idx+5];

            progress += speed;

            if (progress >= 1) {
                curX = tarX; curY = tarY; progress = 0;
                
                const candidates = [
                    {x: curX, y: curY - 1}, {x: curX, y: curY + 1}, 
                    {x: curX - 1, y: curY}, {x: curX + 1, y: curY}
                ].filter(c => validCoords.has(`${c.x},${c.y}`));

                if (candidates.length > 0) {
                    const next = candidates[Math.floor(Math.random() * candidates.length)];
                    tarX = next.x; tarY = next.y;
                } else {
                     const rnd = roadTiles[Math.floor(Math.random() * roadTiles.length)];
                     curX = rnd.x; curY = rnd.y; tarX = rnd.x; tarY = rnd.y;
                }
            }

            carState.current[idx] = curX; carState.current[idx+1] = curY;
            carState.current[idx+2] = tarX; carState.current[idx+3] = tarY;
            carState.current[idx+4] = progress;

            const gx = MathUtils.lerp(curX, tarX, progress);
            const gy = MathUtils.lerp(curY, tarY, progress);
            const [wx, _, wz] = gridToWorld(gx, gy);

            const angle = Math.atan2(tarX - curX, tarY - curY);
            
            dummy.position.set(wx, 0.15, wz);
            dummy.rotation.set(0, angle, 0);
            dummy.position.y += Math.sin(progress * Math.PI * 4) * 0.02;

            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        }
        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    return <instancedMesh ref={meshRef} args={[carGeo, undefined, carCount]}><meshStandardMaterial color="#fcd34d" roughness={0.2} /></instancedMesh>;
};

export const ZoneOverlay = React.memo(({ grid }: { grid: Grid }) => {
    return (
        <group position={[0, 0.01, 0]}>
            {grid.map((row, y) => row.map((tile, x) => {
                const conf = BUILDINGS[tile.buildingType];
                if (conf && conf.zone !== ZoneType.None && tile.isConnected) {
                    let color = 'white';
                    if (conf.zone === ZoneType.DMZ) color = '#00f2ff';
                    if (conf.zone === ZoneType.Public) color = '#4ade80';
                    if (conf.zone === ZoneType.Restricted) color = '#a855f7';
                    if (conf.zone === ZoneType.Security) color = '#d946ef';
                    if (conf.zone === ZoneType.Legal) color = '#c084fc';
                    if (conf.zone === ZoneType.Ops) color = '#fb923c';
                    if (conf.zone === ZoneType.Core) color = '#fbbf24';
                    
                    const [wx, _, wz] = gridToWorld(x, y);
                    return (
                        <mesh key={`${x}-${y}`} position={[wx, 0, wz]} rotation={[-Math.PI/2, 0, 0]}>
                            <planeGeometry args={[0.9, 0.9]} />
                            <meshBasicMaterial color={color} transparent opacity={0.2} />
                        </mesh>
                    );
                }
                return null;
            }))}
        </group>
    );
});
